import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { 
  API_URL, ORDER_STATUSES, STATUS_LABELS, STATUS_COLORS, formatDate, formatCurrency, getWhatsAppLink 
} from '@/lib/utils';
import { toast } from 'sonner';
import {
  Wrench, Moon, Sun, ArrowLeft, MapPin, Printer, MessageCircle,
  Laptop, User, Phone, Mail, DollarSign, FileText, Clock, Loader2,
  Package, CheckCircle2, Search, AlertCircle, Trash2, AlertTriangle
} from 'lucide-react';

const STATUS_ICONS = {
  received: Package,
  diagnosis: Search,
  waiting_parts: Clock,
  maintenance: Wrench,
  software_test: Laptop,
  ready: CheckCircle2,
  delivered: CheckCircle2,
  warranty: AlertCircle
};

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  // Estados principales
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // Estados para diálogos
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showLogDialog, setShowLogDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [logNotes, setLogNotes] = useState('');
  const [editLocation, setEditLocation] = useState('');
  
  // Estados para Finanzas (Edición)
  const [isEditingFinance, setIsEditingFinance] = useState(false);
  const [loadingFinance, setLoadingFinance] = useState(false);
  const [editFinance, setEditFinance] = useState({
    additional_cost: 0,
    advance_payment: 0
  });

  const qrRef = useRef(null);

  // Carga inicial de la orden
  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  // Sincronizar datos de finanzas cuando la orden carga
  useEffect(() => {
    if (order) {
      setEditFinance({
        additional_cost: Number(order.additional_cost) || 0,
        advance_payment: Number(order.advance_payment) || 0
      });
      setEditLocation(order.location || '');
    }
  }, [order]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API_URL}/orders/${orderId}`);
      setOrder(response.data);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar la orden');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  // --- CÁLCULOS MATEMÁTICOS EN VIVO ---
  const servicesCost = Number(order?.services_cost) || 0;
  
  const activeAdditionalCost = isEditingFinance 
    ? (Number(editFinance.additional_cost) || 0) 
    : (Number(order?.additional_cost) || 0);

  const activeAdvancePayment = isEditingFinance 
    ? (Number(editFinance.advance_payment) || 0) 
    : (Number(order?.advance_payment) || 0);

  const currentTotal = servicesCost + activeAdditionalCost;
  const currentSaldo = currentTotal - activeAdvancePayment;

  // --- MANEJADORES DE ESTADO Y LOGS ---

  const handleStatusChange = async () => {
    if (!newStatus) return;
    setUpdating(true);
    try {
      await axios.put(`${API_URL}/orders/${orderId}`, {
        status: newStatus,
        technical_notes: statusNotes
      });
      toast.success('Estado actualizado');
      setShowStatusDialog(false);
      setStatusNotes('');
      fetchOrder();
    } catch (error) {
      toast.error('Error al actualizar estado');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddLog = async () => {
    if (!logNotes) return;
    setUpdating(true);
    try {
      await axios.post(`${API_URL}/orders/${orderId}/log?notes=${encodeURIComponent(logNotes)}`);
      toast.success('Nota agregada');
      setShowLogDialog(false);
      setLogNotes('');
      fetchOrder();
    } catch (error) {
      toast.error('Error al agregar nota');
    } finally {
      setUpdating(false);
    }
  };

  const handleLocationUpdate = async () => {
    if (editLocation === order.location) return;
    setUpdating(true);
    try {
      // Cambia ${orderId} por ${order.id} si 'id' es el número de la base de datos
await axios.put(`${API_URL}/orders/${order.id}/`, { location: editLocation });
      toast.success('Ubicación actualizada');
      fetchOrder();
    } catch (error) {
      toast.error('Error al actualizar ubicación');
    } finally {
      setUpdating(false);
    }
  };

  // --- MANEJADOR DE FINANZAS CORREGIDO ---
  const handleSaveFinance = async () => {
    setLoadingFinance(true);
    try {
      const totalCalculado = servicesCost + Number(editFinance.additional_cost);

      const payload = {
        additional_cost: Number(editFinance.additional_cost),
        estimated_cost: totalCalculado,
        advance_payment: Number(editFinance.advance_payment)
      };

      // ✅ CORRECCIÓN: Usar orderId en lugar de order.order_id
      await axios.put(`${API_URL}/orders/${orderId}`, payload);
      toast.success("Precios actualizados correctamente");
      setIsEditingFinance(false);
      fetchOrder(); 
    } catch (error) {
      console.error(error);
      toast.error("Error al guardar los precios");
    } finally {
      setLoadingFinance(false);
    }
  };

  // --- FUNCIÓN ELIMINAR ORDEN CORREGIDA ---
  const handleDeleteOrder = async () => {
    setUpdating(true);
    try {
      await axios.delete(`${API_URL}/orders/${orderId}`);
      toast.success("Orden eliminada permanentemente");
      navigate('/admin');
    } catch (error) {
      console.error(error);
      toast.error("Error al eliminar la orden");
    } finally {
      setUpdating(false);
      setShowDeleteDialog(false); // ✅ Cerrar diálogo siempre
    }
  };

  // --- GENERACIÓN DE PDF ---
  const generatePDF = () => {
    if (!order) return;
    
    const doc = new jsPDF({
      unit: 'mm',
      format: [80, 150]
    });

    // Header
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('TechFix Pro', 40, 10, { align: 'center' });
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('Centro de Soporte Técnico', 40, 15, { align: 'center' });
    
    // Order ID
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(String(order.order_id), 40, 25, { align: 'center' });
    
    // Divider
    doc.setDrawColor(150);
    doc.setLineWidth(0.1);
    doc.line(5, 30, 75, 30);
    
    // Client Info
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('CLIENTE:', 5, 37);
    doc.setFont('helvetica', 'normal');
    doc.text(order.client.name, 5, 42);
    doc.text(order.client.phone, 5, 47);
    
    // Equipment
    doc.setFont('helvetica', 'bold');
    doc.text('EQUIPO:', 5, 55);
    doc.setFont('helvetica', 'normal');
    doc.text(`${order.equipment.type}: ${order.equipment.brand} ${order.equipment.model}`, 5, 60);
    if (order.equipment.serial) {
      doc.text(`S/N: ${order.equipment.serial}`, 5, 65);
    }
    
    // Divider
    doc.line(5, 85, 75, 85);
    
    // Costs
    doc.setFont('helvetica', 'bold');
    doc.text('PRESUPUESTO:', 5, 92);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total: ${formatCurrency(currentTotal)}`, 5, 97);
    doc.text(`Anticipo: ${formatCurrency(activeAdvancePayment)}`, 5, 102);
    doc.setFont('helvetica', 'bold');
    doc.text(`Resto: ${formatCurrency(currentSaldo)}`, 5, 107);
    
    // QR Code
    const qrCanvas = document.querySelector('#qr-code-canvas canvas');
    if (qrCanvas) {
      const qrDataUrl = qrCanvas.toDataURL('image/png');
      doc.addImage(qrDataUrl, 'PNG', 25, 112, 30, 30);
    }
    
    // Footer
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text('Escanea para rastrear tu equipo', 40, 145, { align: 'center' });
    
    doc.save(`ticket-${order.order_id}.pdf`);
    toast.success('Ticket generado');
  };

  // --- RENDER ---

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!order) return null;

  const Icon = STATUS_ICONS[order.status] || Package;

  return (
    <div className="min-h-screen bg-background pb-10">
      {/* Header */}
      <nav className="border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/admin">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                  <Wrench className="w-4 h-4 text-primary-foreground" />
                </div>
                <span className="font-mono font-bold">Orden #{order.order_id}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={toggleTheme}>
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
              <Button variant="outline" onClick={generatePDF}>
                <Printer className="w-4 h-4 mr-2" />
                Ticket
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          
          {/* COLUMNA IZQUIERDA (Principal) */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Status Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Estado actual</p>
                      <span className={`status-badge ${STATUS_COLORS[order.status]}`}>
                        {STATUS_LABELS[order.status]}
                      </span>
                    </div>
                  </div>
                  <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
                    <DialogTrigger asChild>
                      <Button>Cambiar Estado</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Actualizar Estado</DialogTitle>
                        <DialogDescription>
                          Se notificará al cliente por email.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Nuevo estado</Label>
                          <Select value={newStatus} onValueChange={setNewStatus}>
                            <SelectTrigger>
                              <SelectValue placeholder="Seleccionar estado" />
                            </SelectTrigger>
                            <SelectContent>
                              {ORDER_STATUSES.map(status => (
                                <SelectItem key={status} value={status}>
                                  {STATUS_LABELS[status]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Notas técnicas (opcional)</Label>
                          <Textarea
                            placeholder="Describe el trabajo realizado..."
                            value={statusNotes}
                            onChange={(e) => setStatusNotes(e.target.value)}
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowStatusDialog(false)}>
                          Cancelar
                        </Button>
                        <Button onClick={handleStatusChange} disabled={updating || !newStatus}>
                          {updating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          Actualizar
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>

                {/* Location */}
                <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg">
                  <MapPin className="w-5 h-5 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Ubicación física en taller</p>
                    <Input
                      value={editLocation}
                      onChange={(e) => setEditLocation(e.target.value)}
                      onBlur={handleLocationUpdate}
                      className="mt-1 h-8"
                      placeholder="Ej: Estante A1..."
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Client Info */}
            <Card>
              <CardHeader>
                <CardTitle className="font-mono text-lg flex items-center gap-2">
                  <User className="w-5 h-5" /> Cliente
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Nombre</p>
                    <p className="font-medium">{order.client.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Teléfono / WhatsApp</p>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{order.client.phone}</p>
                      <a
                        href={getWhatsAppLink(order.client.phone, `Hola! Sobre tu orden #${order.order_id}...`)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-green-600">
                          <MessageCircle className="w-4 h-4" />
                        </Button>
                      </a>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{order.client.email || 'No registrado'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Equipment Info */}
            <Card>
              <CardHeader>
                <CardTitle className="font-mono text-lg flex items-center gap-2">
                  <Laptop className="w-5 h-5" /> Equipo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Dispositivo</p>
                    <p className="font-medium">{order.equipment.type} - {order.equipment.brand}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Modelo</p>
                    <p className="font-medium">{order.equipment.model}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Serie (S/N)</p>
                    <p className="font-mono">{order.equipment.serial || 'N/A'}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-sm text-muted-foreground">Estado Estético</p>
                    <p className="text-sm">{order.equipment.aesthetic_condition || 'Sin observaciones'}</p>
                  </div>
                </div>
                {order.accessories && order.accessories.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground mb-2">Accesorios Recibidos</p>
                    <div className="flex flex-wrap gap-2">
                      {order.accessories.map((acc, i) => (
                        <span key={i} className="px-2 py-1 bg-secondary rounded-md text-xs font-medium">
                          {acc}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Diagnosis */}
            <Card>
              <CardHeader>
                <CardTitle className="font-mono text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5" /> Diagnóstico y Falla
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Falla Reportada</p>
                  <div className="p-3 bg-muted/20 rounded-lg text-sm mt-1">
                    {order.reported_issue}
                  </div>
                </div>
                {order.technical_notes && (
                  <div>
                    <p className="text-sm text-muted-foreground">Notas del Técnico</p>
                    <p className="text-sm mt-1">{order.technical_notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Timeline / Logs */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-mono text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5" /> Bitácora
                </CardTitle>
                <Dialog open={showLogDialog} onOpenChange={setShowLogDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">Agregar nota</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Agregar nota interna</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                      <Textarea
                        placeholder="Escribe una observación..."
                        value={logNotes}
                        onChange={(e) => setLogNotes(e.target.value)}
                        rows={4}
                      />
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowLogDialog(false)}>Cancelar</Button>
                      <Button onClick={handleAddLog} disabled={updating || !logNotes}>Agregar</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-6 relative pl-4 border-l-2 border-muted">
                  {order.logs && order.logs.slice().reverse().map((log, index) => {
                     const LogIcon = STATUS_ICONS[log.status] || Package;
                     return (
                      <div key={log.log_id || index} className="relative">
                        <div className={`absolute -left-[21px] top-0 w-3 h-3 rounded-full border-2 bg-background ${index === 0 ? 'border-primary' : 'border-muted'}`} />
                        <div className="flex items-center gap-2 mb-1">
                           <LogIcon className="w-3 h-3 text-muted-foreground" />
                           <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[log.status]}`}>
                             {STATUS_LABELS[log.status]}
                           </span>
                           <span className="text-xs text-muted-foreground ml-auto">
                             {formatDate(log.timestamp)}
                           </span>
                        </div>
                        <p className="text-sm">{log.notes}</p>
                        {log.technician && (
                          <p className="text-xs text-muted-foreground mt-1">Por: {log.technician}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* COLUMNA DERECHA (Sidebar) */}
          <div className="space-y-6">
            
            {/* GESTIÓN FINANCIERA */}
            <Card className="border-2 shadow-lg overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between border-b bg-muted/20 py-3">
                <CardTitle className="font-black italic uppercase text-sm flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-primary" />
                  Finanzas
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setIsEditingFinance(!isEditingFinance)}
                  className="text-[10px] font-bold uppercase h-6 px-2"
                >
                  {isEditingFinance ? 'Cancelar' : 'Editar'}
                </Button>
              </CardHeader>
              
              <CardContent className="p-5 space-y-4">
                {/* 1. Mano de Obra */}
                <div className="flex justify-between items-center text-sm">
                   <span className="text-muted-foreground">Mano de Obra (Base):</span>
                   <span className="font-medium">{formatCurrency(servicesCost)}</span>
                </div>

                {/* 2. Repuestos / Extras */}
                <div className="flex justify-between items-center text-sm">
                   <span className="text-muted-foreground">Repuestos / Extras:</span>
                   {isEditingFinance ? (
                      <Input 
                        type="number" 
                        className="w-24 h-7 text-right font-bold text-amber-600 border-amber-200" 
                        value={editFinance.additional_cost}
                        onChange={(e) => setEditFinance({...editFinance, additional_cost: e.target.value})}
                      />
                   ) : (
                      <span className="font-bold text-amber-600">+{formatCurrency(activeAdditionalCost)}</span>
                   )}
                </div>

                {/* 3. Anticipo */}
                <div className="flex justify-between items-center text-sm">
                   <span className="text-muted-foreground">Anticipo:</span>
                   {isEditingFinance ? (
                      <Input 
                        type="number" 
                        className="w-24 h-7 text-right font-bold text-green-600 border-green-200" 
                        value={editFinance.advance_payment}
                        onChange={(e) => setEditFinance({...editFinance, advance_payment: e.target.value})}
                      />
                   ) : (
                      <span className="font-bold text-green-600">-{formatCurrency(activeAdvancePayment)}</span>
                   )}
                </div>

                {/* 4. TOTALES CALCULADOS EN VIVO */}
                <div className="border-t-2 border-dashed pt-3 mt-2">
                  <div className="flex justify-between items-end mb-1">
                     <span className="text-[10px] font-black uppercase text-muted-foreground">Total Orden</span>
                     <span className="text-sm font-bold">{formatCurrency(currentTotal)}</span>
                  </div>
                  <div className="flex justify-between items-end bg-primary/5 p-2 rounded">
                     <span className="text-[10px] font-black uppercase text-primary">Saldo Pendiente</span>
                     <span className="text-2xl font-black text-primary leading-none">
                       {formatCurrency(currentSaldo)}
                     </span>
                  </div>
                </div>

                {/* Botón Guardar */}
                {isEditingFinance && (
                   <Button 
                     className="w-full font-bold shadow-md" 
                     onClick={handleSaveFinance}
                     disabled={loadingFinance}
                   >
                     {loadingFinance ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : null}
                     Guardar Cambios
                   </Button>
                )}
              </CardContent>
            </Card>

            {/* QR Code */}
            <Card>
              <CardHeader>
                <CardTitle className="font-mono text-lg">Código de Rastreo</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                <div id="qr-code-canvas" className="p-4 bg-white rounded-lg border shadow-sm">
                  <QRCodeSVG
                    value={`${window.location.origin}/track/${order.order_id}`}
                    size={140}
                    level="M"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-4 text-center px-4">
                  El cliente puede escanear esto para ver el estado en tiempo real.
                </p>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="font-mono text-lg">Acciones Rápidas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <a
                  href={getWhatsAppLink(
                    order.client.phone,
                    `Hola ${order.client.name}! Tu equipo (${order.equipment.brand} ${order.equipment.model}) está ${STATUS_LABELS[order.status]}. Orden: #${order.order_id} - Saldo: ${formatCurrency(currentSaldo)}`
                  )}
                  target="_blank"
                  rel="noreferrer"
                  className="block"
                >
                  <Button variant="outline" className="w-full justify-start">
                    <MessageCircle className="w-4 h-4 mr-2 text-green-600" />
                    Enviar WhatsApp
                  </Button>
                </a>
                <Button variant="outline" className="w-full justify-start" onClick={generatePDF}>
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir Ticket
                </Button>
              </CardContent>
            </Card>

            {/* ZONA DE PELIGRO (ELIMINAR) */}
            <Card className="border-red-200 bg-red-50 dark:bg-red-950/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-bold text-red-600 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Zona de Peligro
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                  <DialogTrigger asChild>
                    <Button variant="destructive" className="w-full bg-red-600 hover:bg-red-700">
                      <Trash2 className="w-4 h-4 mr-2" /> Eliminar Orden
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="border-red-500">
                    <DialogHeader>
                      <DialogTitle className="text-red-600 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" /> ¿Estás seguro?
                      </DialogTitle>
                      <DialogDescription>
                        Esta acción no se puede deshacer. Se eliminará permanentemente la orden <strong>#{order.order_id}</strong> y todo su historial de la base de datos.
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-0">
                      <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancelar</Button>
                      <Button variant="destructive" onClick={handleDeleteOrder} disabled={updating}>
                        {updating ? "Eliminando..." : "Sí, eliminar orden"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    </div>
  );
}